import Button from "components/ui/Button";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import { Plus, Trash2, Upload, X } from "lucide-react";
import React, { useRef, useState } from "react";
import { useToast } from "../../../contexts/ToastContext";

interface SalesProperty {
	id?: string;
	title?: string;
	address?: string;
	type?: string;
	price?: number;
	status?: string;
	bedrooms?: number;
	bathrooms?: number;
	square_feet?: number;
	squareFootage?: number;
	images?: string[];
	description?: string;
	created_at?: string;
	viewings?: number;
	inquiries?: number;
	propertyType?: string;
}

interface ListPropertyModalProps {
	property?: SalesProperty | null;
	onClose: () => void;
	onSave: (propertyData: Partial<SalesProperty>) => void;
	isLoading?: boolean;
}

const ListPropertyModal: React.FC<ListPropertyModalProps> = ({
	property,
	onClose,
	onSave,
	isLoading = false,
}) => {
	const { error: showError } = useToast();
	const [formData, setFormData] = useState({
		title: property?.title || "",
		address: property?.address || "",
		price: property?.price || "",
		bedrooms: property?.bedrooms || "",
		bathrooms: property?.bathrooms || "",
		squareFootage: property?.squareFootage || property?.square_feet || "",
		propertyType: property?.propertyType || property?.type || "house",
		description: property?.description || "",
		images: property?.images || [],
	});
	const [isDragging, setIsDragging] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	// Update form data when property changes (for editing)
	React.useEffect(() => {
		if (property) {
			setFormData({
				title: property.title || "",
				address: property.address || "",
				price: property.price || "",
				bedrooms: property.bedrooms || "",
				bathrooms: property.bathrooms || "",
				squareFootage: property.squareFootage || property.square_feet || "",
				propertyType: property.propertyType || property.type || "house",
				description: property.description || "",
				images: property.images || [],
			});
		} else {
			// Reset form for new property
			setFormData({
				title: "",
				address: "",
				price: "",
				bedrooms: "",
				bathrooms: "",
				squareFootage: "",
				propertyType: "house",
				description: "",
				images: [],
			});
		}
	}, [property]);

	const handleInputChange = (field: string, value: string | string[]) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
	};

	const handleFileSelect = (files: FileList | null) => {
		if (!files) return;
		const newImages: string[] = [];
		const fileArray = Array.from(files);

		// Allowed image formats: jpg, jpeg, png
		const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
		const allowedExtensions = [".jpg", ".jpeg", ".png"];

		fileArray?.forEach((file) => {
			const isValidType = allowedTypes.includes(file?.type?.toLowerCase());
			const isValidExtension = allowedExtensions.some(ext =>
				file?.name?.toLowerCase().endsWith(ext)
			);

			if (isValidType || isValidExtension) {
				const reader = new FileReader();
				reader.onload = (e) => {
					if (e?.target?.result) {
						newImages?.push(e?.target?.result as string);
						if (newImages?.length === fileArray?.length) {
							setFormData((prev) => ({
								...prev,
								images: [...prev?.images, ...newImages],
							}));
						}
					}
				};
				reader?.readAsDataURL(file);
			} else {
				showError(`File ${file?.name} is not a supported format. Please use JPG, JPEG, or PNG.`);
			}
		});
	};

	const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		e?.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
		e?.preventDefault();
		setIsDragging(false);
	};

	const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
		e?.preventDefault();
		setIsDragging(false);
		const files = e?.dataTransfer?.files;
		handleFileSelect(files);
	};

	const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e?.target?.files;
		if (files) {
			handleFileSelect(files);
		}
	};

	const removeImage = (index: number) => {
		setFormData((prev) => ({
			...prev,
			images: prev?.images?.filter((_, i) => i !== index),
		}));
	};

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e?.preventDefault();

		// Validate required fields
		if (!formData?.title?.trim()) {
			showError("Property title is required.");
			return;
		}

		if (!formData?.address?.trim()) {
			showError("Property address is required.");
			return;
		}

		if (!formData?.price || parseFloat(formData?.price as string) <= 0) {
			showError("Please enter a valid price.");
			return;
		}

		const propertyData = {
			...formData,
			type: formData.propertyType, // Map propertyType to type for API
			price: parseFloat(formData?.price as string) || 0,
			bedrooms: parseInt(formData?.bedrooms as string) || 0,
			bathrooms: parseFloat(formData?.bathrooms as string) || 0,
			squareFootage: parseInt(formData?.squareFootage as string) || 0,
			square_feet: parseInt(formData?.squareFootage as string) || 0,
		};

		onSave(propertyData);
	};

	const propertyTypeOptions = [
		{ value: "house", label: "House" },
		{ value: "apartment", label: "Apartment" },
		{ value: "condo", label: "Condo" },
		{ value: "townhouse", label: "Townhouse" },
		{ value: "commercial", label: "Commercial" },
	];

	return (
		<div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
			<div className="bg-card rounded-lg border border-border w-full max-w-4xl my-8">
				<div className="flex items-center justify-between p-6 border-b border-border">
					<h2 className="text-xl font-semibold text-foreground">
						{property ? "Edit Property" : "List New Property"}
					</h2>
					<Button variant="ghost" size="icon" onClick={onClose}>
						<X className="w-5 h-5" />
					</Button>
				</div>

				<form onSubmit={handleSubmit} className="p-6 space-y-6">
					{/* Basic Information */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						<div>
							<label className="block text-sm font-medium text-foreground mb-2">
								Property Title *
							</label>
							<Input
								value={formData?.title}
								onChange={(e) => handleInputChange("title", e?.target?.value)}
								placeholder="Beautiful 3-bedroom house..."
								required
							/>
						</div>

						<div>
							<label className="block text-sm font-medium text-foreground mb-2">
								Address *
							</label>
							<Input
								value={formData?.address}
								onChange={(e) => handleInputChange("address", e?.target?.value)}
								placeholder="123 Main St, City, State 12345"
								required
							/>
						</div>

						<div>
							<label className="block text-sm font-medium text-foreground mb-2">
								Price *
							</label>
							<Input
								type="number"
								value={formData?.price}
								onChange={(e) => handleInputChange("price", e?.target?.value)}
								placeholder="500000"
								required
							/>
						</div>

						<div>
							<label className="block text-sm font-medium text-foreground mb-2">
								Property Type
							</label>
							<Select
								value={formData?.propertyType}
								onValueChange={(value) =>
									handleInputChange("propertyType", String(value))
								}
								options={propertyTypeOptions}
							/>
						</div>

						<div>
							<label className="block text-sm font-medium text-foreground mb-2">
								Bedrooms
							</label>
							<Input
								type="number"
								value={formData?.bedrooms}
								onChange={(e) =>
									handleInputChange("bedrooms", e?.target?.value)
								}
								placeholder="3"
							/>
						</div>

						<div>
							<label className="block text-sm font-medium text-foreground mb-2">
								Bathrooms
							</label>
							<Input
								type="number"
								step="0.5"
								value={formData?.bathrooms}
								onChange={(e) =>
									handleInputChange("bathrooms", e?.target?.value)
								}
								placeholder="2"
							/>
						</div>
					</div>

					{/* Description */}
					<div>
						<label className="block text-sm font-medium text-foreground mb-2">
							Description
						</label>
						<textarea
							value={formData?.description}
							onChange={(e) =>
								handleInputChange("description", e?.target?.value)
							}
							rows={4}
							className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
							placeholder="Describe the property features, location, and highlights..."
						/>
					</div>

					{/* Image Upload */}
					<div>
						<label className="block text-sm font-medium text-foreground mb-2">
							Property Images
						</label>

						<div
							className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
								isDragging
									? "border-primary bg-primary/5"
									: "border-border hover:border-primary/50"
							}`}
							onDragOver={handleDragOver}
							onDragLeave={handleDragLeave}
							onDrop={handleDrop}
						>
							<div className="flex flex-col items-center">
								<Upload className="w-12 h-12 text-muted-foreground mb-4" />
								<h3 className="text-lg font-medium text-foreground mb-2">
									Upload Property Images
								</h3>
								<p className="text-muted-foreground mb-4">
									Drag and drop images here, or click to browse
								</p>
								<Button
									type="button"
									variant="outline"
									onClick={() => fileInputRef?.current?.click()}
								>
									<Plus className="w-4 h-4 mr-2" />
									Choose Files
								</Button>
								<input
									ref={fileInputRef}
									type="file"
									multiple
									accept="image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png"
									onChange={handleFileInputChange}
									className="hidden"
								/>
							</div>
						</div>

						{/* Image Preview */}
						{formData?.images?.length > 0 && (
							<div className="mt-4">
								<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
									{formData?.images?.map((image, index) => (
										<div key={index} className="relative group">
											<img
												src={image}
												alt={`Property image ${index + 1}`}
												className="w-full h-24 object-cover rounded-lg border border-border"
											/>
											<Button
												type="button"
												variant="ghost"
												size="icon"
												onClick={() => removeImage(index)}
												className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
											>
												<Trash2 className="w-4 h-4" />
											</Button>
										</div>
									))}
								</div>
							</div>
						)}
					</div>

					{/* Form Actions */}
					<div className="flex items-center justify-end gap-3 pt-6 border-t border-border">
						<Button
							type="button"
							variant="outline"
							onClick={onClose}
							disabled={isLoading}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={isLoading}>
							{isLoading
								? "Saving..."
								: property
								? "Update Property"
								: "List Property"}
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default ListPropertyModal;
